import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { Select } from "components/form_components/Inputs";
import { useMyFeedPageState } from "components/my_feed/useMyFeedPageState";
import { generateNoticeSearchUrl } from "components/notices/utils";
import { StrategyPaywallContent } from "components/paywall/strategy_paywall/StrategyPaywallContent";
import Button from "components/ui/button/Button";
import { useSubscription } from "lib/hooks/subscription";
import { useURLState } from "lib/hooks/useURLState";
import { EventNames, useTracking } from "lib/tracking";
import {
  NoticeAnalysisByGroupGroupBy,
  NoticeAnalysisByPeriodGroupBy,
  NoticeAnalysisByPeriodInterval,
} from "lib/types/graphQLEnums";
import CentredSpinner from "../../lib/core_components/CentredSpinner";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import ByGroup from "./ByGroup";
import ByPeriod from "./ByPeriod";
import { NoticeAnalysisExportButton } from "./ExportButton";

import StotlesLogoText from "../../../assets/images/stotles_logo_and_typeface_print.svg";

type NoticeAnalysisForm = {
  groupBy: NoticeAnalysisByGroupGroupBy | NoticeAnalysisByPeriodGroupBy;
  interval: NoticeAnalysisByPeriodInterval;
  chartShow: "noticeCount" | "noticeValues";
};

const DEFAULT_VALUES: NoticeAnalysisForm = {
  groupBy: NoticeAnalysisByGroupGroupBy.Buyer,
  interval: NoticeAnalysisByPeriodInterval.Month, // only shown if groupBy is for a time period
  chartShow: "noticeCount", // only shown if groupBy is for a time period
};

function NoticeAnalysisContent() {
  const { logEvent } = useTracking();
  const subscription = useSubscription();

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const {
    filters: noticeViewFilters,
    setFilters: setNoticeViewFilters,
    selectedView,
    isLoadingViews,
  } = useMyFeedPageState();
  const [analysisFilters = DEFAULT_VALUES, setAnalysisFilters] = useURLState<
    NoticeAnalysisForm | undefined
  >("analysis", DEFAULT_VALUES);

  const { control, watch, getValues, trigger } = useForm<NoticeAnalysisForm>({
    mode: "onChange",
    defaultValues: analysisFilters,
  });

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: (newValue) => {
      setAnalysisFilters(newValue);

      if (
        newValue.groupBy !== analysisFilters.groupBy ||
        newValue.interval !== analysisFilters.interval
      ) {
        setSelectedRows([]); // Reset selected rows when rows change
      }
    },
  });

  useEffect(() => {
    const isPeriod = byPeriod(analysisFilters);

    logEvent(EventNames.marketAnalysisFilterChanged, {
      grouping: analysisFilters.groupBy,
      measure: isPeriod ? analysisFilters.chartShow : "N/A",
      interval: isPeriod ? analysisFilters.interval : "N/A",
    });
  }, [analysisFilters, logEvent]);

  useEffect(() => {
    const onBeforePrint = () => {
      logEvent(EventNames.marketAnalysisExported, {
        exportType: "print",
        grouping: analysisFilters.groupBy,
        measure: byPeriod(analysisFilters) ? analysisFilters.chartShow : "N/A",
        interval: byPeriod(analysisFilters) ? analysisFilters.interval : "N/A",
      });
    };
    window.addEventListener("beforeprint", onBeforePrint);
    return () => {
      window.removeEventListener("beforeprint", onBeforePrint);
    };
  }, [logEvent, analysisFilters]);

  if (!subscription.hasDataTypes("MARKET_ANALYSIS")) {
    return (
      <PaywallContainer>
        <StrategyPaywallContent
          featureType={"STRATEGY"}
          trackingData={{ "Page source": "Notice Analysis - Paywall" }}
        />
      </PaywallContainer>
    );
  }

  if (isLoadingViews) {
    return <CentredSpinner />;
  }

  return (
    <Flex column width="100%" height="100vh">
      <BreadcrumbContainer>
        <Flex gap="8px" alignItems="center">
          <Button
            icon="searchRefraction"
            buttonStyle="inverse"
            as="a"
            href={generateNoticeSearchUrl(noticeViewFilters, selectedView?.id)}
          >
            {selectedView?.name ?? "Unnamed view"}
          </Button>
          /
          <CurrentBreadcrumb buttonStyle="inverse" icon="barChart12">
            Analysis
          </CurrentBreadcrumb>
        </Flex>
        <NoticeAnalysisExportButton
          filters={noticeViewFilters}
          selectedRows={selectedRows}
          groupBy={analysisFilters.groupBy}
        />
        {/* <Button icon="share07">
          Share
          </Button> */}
      </BreadcrumbContainer>
      <Flex flexGrow={1}>
        <ContentWrapper column gap={24}>
          {byPeriod(analysisFilters) ? (
            <ByPeriod
              filters={noticeViewFilters}
              groupBy={analysisFilters.groupBy as NoticeAnalysisByPeriodGroupBy}
              interval={analysisFilters.interval}
              chartShow={analysisFilters.chartShow}
              setFilters={setNoticeViewFilters}
              selectedView={selectedView}
            />
          ) : (
            <ByGroup
              filters={noticeViewFilters}
              groupBy={analysisFilters.groupBy as NoticeAnalysisByGroupGroupBy}
              setFilters={setNoticeViewFilters}
              selectedView={selectedView}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          )}
        </ContentWrapper>
        <SidebarContainer column gap={10}>
          <Text h2>Trend analysis</Text>
          <form>
            <Select
              name="groupBy"
              label="Grouped by"
              control={control}
              options={[
                { label: "Buyers", value: NoticeAnalysisByGroupGroupBy.Buyer },
                { label: "Suppliers", value: NoticeAnalysisByGroupGroupBy.Supplier },
                {
                  label: "Published date",
                  value: NoticeAnalysisByPeriodGroupBy.PublishDate,
                },
                {
                  label: "Expiry date",
                  value: NoticeAnalysisByPeriodGroupBy.ExpiryDate,
                },
                {
                  label: "Award date",
                  value: NoticeAnalysisByPeriodGroupBy.AwardDate,
                },
              ]}
              optionFilterProp="label"
            />
            {byPeriod(analysisFilters) && (
              <>
                <Select
                  name="chartShow"
                  label="Measure"
                  control={control}
                  options={[
                    { label: "Notice count", value: "noticeCount" },
                    { label: "Total value", value: "noticeValues" },
                  ]}
                  optionFilterProp="label"
                />
                <Select
                  name="interval"
                  label="Interval"
                  control={control}
                  options={[
                    { label: "Monthly", value: NoticeAnalysisByPeriodInterval.Month },
                    { label: "Quarterly", value: NoticeAnalysisByPeriodInterval.Quarter },
                    { label: "Yearly", value: NoticeAnalysisByPeriodInterval.Year },
                  ]}
                  optionFilterProp="label"
                />
              </>
            )}
          </form>
        </SidebarContainer>
      </Flex>
    </Flex>
  );
}

const BreadcrumbContainer = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  padding: "24px",
  borderBottom: `1px solid ${theme.colors.sysBorderPrimary}`,
  "@media print": {
    display: "none",
  },
}));

const ContentWrapper = styled(Flex)(({ theme }) => ({
  padding: "24px",
  borderRight: `1px solid ${theme.colors.sysBorderPrimary}`,
  height: "100%",
  width: "100%",
  maxWidth: "calc(100% - 360px)",
  "@media print": {
    padding: 0,
    "@page": {
      fontFamily: theme.fontFamily,
      fontSize: "12px",
      // Currently the custom header and footer doesn't work in Firefox or Safari since they don't support
      // this CSS feature. It will show their default header and footer instead
      // https://caniuse.com/mdn-css_at-rules_page_top-left
      marginTop: "1.8cm",
      "@top-left": {
        // You can't resize this image for some reason, even though you can normally with content elsewhere
        // so I've just resized the SVG itself to make it fit
        content: `url("${StotlesLogoText}") / "Stotles"`,
      },
      "@bottom-left": {
        content: `"Created with Stotles, ${new Date().toLocaleDateString("en-GB", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })} by ${window.currentUser?.first_name ?? ""} ${window.currentUser?.last_name ?? ""}"`,
      },
      "@bottom-right": {
        content: '"Page " counter(page) " of " counter(pages)',
      },
    },
    maxWidth: "100%",
    borderRight: "none",
  },
}));

const SidebarContainer = styled(Flex)(({ theme }) => ({
  width: "360px",
  padding: "24px",
  borderBottom: `1px solid ${theme.colors.sysBorderPrimary}`,
  "@media print": {
    display: "none",
  },
}));

const PaywallContainer = styled(Flex)({
  height: "80%",
});

const CurrentBreadcrumb = styled(Button)({
  "&:hover": {
    cursor: "default",
    background: "none",
  },
});

const byPeriod: (form: NoticeAnalysisForm) => boolean = (form) => {
  return Object.values(NoticeAnalysisByPeriodGroupBy).includes(
    form.groupBy as NoticeAnalysisByPeriodGroupBy,
  );
};
export default NoticeAnalysisContent;
